<script setup lang="ts">
import { type PostitAddress } from "@/composables/usePostit";
import { ApiClientError } from "@shopware/api-client";
import useVuelidate from "@vuelidate/core";
import AutoComplete, {
  type AutoCompleteCompleteEvent,
  type AutoCompleteOptionSelectEvent,
} from "primevue/autocomplete";
import { useUser } from "~/composables/useUser";
import { customValidators } from "~/i18n/utils/i18n-validators";

const { register: _register } = useUser();
const { countryId } = useSessionContext();
const { required, email, minLength, sameAs, phoneNumber } = customValidators();
const { newsletterSubscribe } = useNewsletter();
const { getCountries } = useCountries();
const { fetchSuggestions: _fetchSuggestions, suggestions } = usePostit();
const { trackSignUp } = useGtmEvents()
const { t } = useI18n();
const config = useRuntimeConfig();
const toast = useToast();
const route = useRoute();
const isLoading = ref(false);

const addressSearchTerm = ref<string | null>("");
const dialCode = ref("+370");
const _phoneNumber = ref("");
const wholeNumber = computed(() =>
  dialCode.value && _phoneNumber.value
    ? `${dialCode.value}${_phoneNumber.value}`
    : ""
);

const ltFlag = "🇱🇹";
const lithuania = computed(() =>
  getCountries.value?.find((country) => country.iso === "LT")
);

function clearBillingAddress() {
  formData.billingAddress.street = "";
  formData.billingAddress.zipcode = "";
  formData.billingAddress.city = "";
}

const fetchAddressSuggestions = useDebounceFn(
  async (event: AutoCompleteCompleteEvent) => {
    // @ts-expect-error
    if (event.originalEvent?.inputType === "deleteContentBackward") {
      clearBillingAddress();
    }

    if (addressSearchTerm.value && addressSearchTerm.value.length > 3) {
      return await _fetchSuggestions({
        term: addressSearchTerm.value,
        wide_number: 1,
        limit: 15,
      });
    }
  },
  300
);

const formData = reactive({
  salutationId: undefined,
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  repeatPassword: "",
  guest: false,
  accountType: "private",
  acceptedDataProtection: false,
  storefrontUrl: `${config.public.shopware.devStorefrontUrl}`,
  billingAddress: {
    phoneNumber: computed(() => wholeNumber.value),
    street: "",
    zipcode: "",
    city: "",
    countryId: computed(() => lithuania.value?.id || countryId.value),
    additionalAddressLine1: "",
  },
  newsletter: false,
});

const formRules = computed(() => ({
  firstName: {
    required,
  },
  lastName: {
    required,
  },
  email: {
    required,
    email,
  },
  password: {
    required,
    minLength: minLength(8),
  },
  repeatPassword: {
    required,
    sameAs: sameAs(formData.password, t("form.password")),
  },
  acceptedDataProtection: {
    required,
    isTrue: (value: boolean): boolean => value === true,
  },
  billingAddress: {
    phoneNumber: {
      required,
      phoneNumber,
    },
    zipcode: {
      required,
    },
    city: {
      required,
    },
    street: {
      required,
    },
    countryId: {
      required,
    },
  },
}));

const $v = useVuelidate(formRules, formData);
const { resolveApiErrors } = useApiErrorsResolver("account_login");

async function register() {
  const isFormCorrect = await $v.value.$validate();
  isLoading.value = true;

  try {
    if (isFormCorrect) {
      await _register(formData);

      toast.add({
        severity: "success",
        summary: t("account.messages.loggedInSuccess"),
        life: 3000,
      });

      trackSignUp()

      if (formData.newsletter) {
        newsletterSubscribe({
          option: "direct",
          email: formData.email,
        });
      }

      await navigateTo({
        query: { ...route.query, modal: undefined, register: undefined },
      });
    }
  } catch (error) {
    if (error instanceof ApiClientError) {
      const errors = resolveApiErrors(error.details.errors);
      console.error(errors);

      toast.add({
        severity: "error",
        summary: t(`error.${error.details.errors[0].code}`),
        life: 3000,
      });
    }
  } finally {
    isLoading.value = false;
  }
}

function formatLabel(data: PostitAddress) {
  return `${data.address}, ${data?.city}, ${data?.municipality}`;
}

function selectAddress(event: AutoCompleteOptionSelectEvent) {
  const { address, post_code, city } = event.value;

  formData.billingAddress.street = address;
  formData.billingAddress.zipcode = post_code;
  formData.billingAddress.city = city;
}

watch(
  addressSearchTerm,
  (newValue) => {
    if (newValue === "") {
      clearBillingAddress();
    }
  },
  { deep: true }
);
</script>

<template>
  <form
    id="registration-form"
    class="flex flex-col gap-4"
    @submit.prevent="register"
  >
    <div class="grid gap-2">
      <label for="firstName">{{ $t("form.firstName") }}</label>
      <InputText
        id="firstName"
        v-model="formData.firstName"
        name="firstName"
        type="text"
        :aria-label="$t('form.firstName')"
        :placeholder="$t('form.firstNamePlaceholder')"
        :invalid="$v.firstName.$error"
        aria-autocomplete="list"
        @blur="$v.firstName.$touch()"
      />

      <span v-if="$v.firstName.$error" class="text-rose-500 text-sm">
        {{ $v.firstName.$errors[0].$message }}
      </span>
    </div>

    <div class="grid gap-2">
      <label for="lastName">{{ $t("form.lastName") }}</label>
      <InputText
        id="lastName"
        v-model="formData.lastName"
        type="text"
        :aria-label="$t('form.lastName')"
        :placeholder="$t('form.lastNamePlaceholder')"
        :invalid="$v.lastName.$error"
        @blur="$v.lastName.$touch()"
      />

      <span v-if="$v.lastName.$error" class="text-rose-500 text-sm">
        {{ $v.lastName.$errors[0].$message }}
      </span>
    </div>

    <div class="grid gap-2">
      <label for="phoneNumber">{{ $t("form.phoneNumber") }}</label>
      <InputGroup>
        <InputText :value="`${ltFlag} ${dialCode}`" readonly class="w-24" />

        <InputText
          id="phoneNumber"
          v-model="_phoneNumber"
          :aria-label="$t('form.phoneNumber')"
          :placeholder="$t('form.phoneNumberPlaceholder')"
          :invalid="$v.billingAddress.phoneNumber.$error"
          class="w-full"
          @blur="$v.billingAddress.phoneNumber.$touch()"
        />
      </InputGroup>

      <span
        v-if="$v.billingAddress.phoneNumber.$error"
        class="text-rose-500 text-sm"
      >
        {{ $t(`${$v.billingAddress.phoneNumber.$errors[0].$message}`) }}
      </span>
    </div>

    <div class="grid gap-2">
      <label for="email">{{ $t("form.email") }}</label>
      <InputText
        id="email"
        v-model="formData.email"
        type="email"
        :aria-label="$t('form.email')"
        :placeholder="$t('form.emailPlaceholder', { sign: '@' })"
        :invalid="$v.email.$error"
        @blur="$v.email.$touch()"
      />

      <span v-if="$v.email.$error" class="text-rose-500 text-sm">
        {{ $v.email.$errors[0].$message }}
      </span>
    </div>

    <div class="grid gap-2">
      <label for="address">{{ $t("form.streetAddress") }}</label>

      <AutoComplete
        v-model="addressSearchTerm"
        input-id="address"
        input-class="w-full"
        :min-length="4"
        force-selection
        :suggestions="suggestions"
        :aria-label="$t('form.streetAddress')"
        :placeholder="$t('form.streetPlaceholder')"
        :emptySearchMessage="$t('form.streetNotFoundPlaceholder')"
        :option-label="(data: PostitAddress) => formatLabel(data)"
        :pt="{
          list: 'px-2 py-3 grid gap-1',
          item: 'text-sm py-2 px-4 hover:ring-1 data-[p-focus=true]:ring-1 ring-primary-500 rounded cursor-pointer transition',
        }"
        :invalid="
          $v.billingAddress.city.$error ||
          $v.billingAddress.zipcode.$error ||
          $v.billingAddress.street.$error
        "
        @complete="fetchAddressSuggestions"
        @item-select="selectAddress"
        @blur="$v.billingAddress.street.$touch()"
      />

      <span
        v-if="
          $v.billingAddress.city.$error ||
          $v.billingAddress.zipcode.$error ||
          $v.billingAddress.street.$error
        "
        class="text-rose-500 text-sm"
      >
        {{ $t("validations.required") }}
      </span>
    </div>

    <div class="grid gap-2">
      <label for="appartmentNumber">{{ $t("form.appartmentNumber") }}</label>
      <InputText
        id="appartmentNumber"
        v-model="formData.billingAddress.additionalAddressLine1"
        :aria-label="$t('form.apartmentNumber')"
        :placeholder="$t('form.apartmentNumberPlaceholder')"
      />

      <small class="italic">{{ $t("form.optionalField") }}</small>
    </div>

    <div class="grid gap-2">
      <label for="password">{{ $t("form.password") }}</label>
      <Password
        v-model="formData.password"
        input-id="password"
        toggle-mask
        :feedback="false"
        :aria-label="$t('form.password')"
        :placeholder="$t('form.passwordPlaceholder')"
        :invalid="$v.password.$error"
        input-class="w-full"
        @blur="$v.password.$touch()"
      />
      <small id="password-help" class="text-gray-700">{{
        $t("form.passwordRequirement")
      }}</small>

      <span v-if="$v.password.$error" class="text-rose-500 text-sm">
        {{ $v.password.$errors[0].$message }}
      </span>
    </div>

    <div class="grid gap-2">
      <label for="repeatPassword">{{ $t("form.repeatPassword") }}</label>
      <Password
        v-model="formData.repeatPassword"
        input-id="repeatPassword"
        toggle-mask
        :feedback="false"
        :aria-label="$t('form.repeatPassword')"
        :placeholder="$t('form.repeatPasswordPlaceholder')"
        :invalid="$v.repeatPassword.$error"
        input-class="w-full"
        @blur="$v.repeatPassword.$touch()"
      />

      <span v-if="$v.repeatPassword.$error" class="text-rose-500 text-sm">
        {{ $v.repeatPassword.$errors[0].$message }}
      </span>
    </div>

    <div class="grid gap-2">
      <div class="flex gap-2">
        <Checkbox
          v-model="formData.acceptedDataProtection"
          input-id="data-protection"
          binary
          :invalid="$v.acceptedDataProtection.$error"
        />
        <label for="data-protection">{{
          $t("checkout.termsAdnConditionsLabel")
        }}</label>
      </div>
    </div>

    <div class="grid gap-2">
      <div class="flex gap-2">
        <Checkbox v-model="formData.newsletter" input-id="newsletter" binary />
        <label for="newsletter">{{
          $t("newsletter.subscriptionCheckbox")
        }}</label>
      </div>
    </div>

    <div class="mt-4">
      <Button
        type="submit"
        form="registration-form"
        class="hover:scale-105 duration-400 transition-transform cursor-pointer"
        :loading="isLoading"
        :label="$t('form.register')"
        rounded
      >
      </Button>
    </div>
  </form>
</template>
